import {
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
  type DragEvent,
  type FormEvent,
} from "react";

import {
  ApiRequestError,
  MAX_UPLOAD_BYTES,
  generateFromUpload,
  loadDemo,
  renderPng,
  type GenerationPreferences,
  type GenerationResult,
  type OutputLanguage,
  type OutputUnit,
} from "./api";
import {
  BrandLogo,
  CheckIcon,
  CloseIcon,
  DataIcon,
  DownloadIcon,
  HelpIcon,
  UploadIcon,
} from "./icons";
import { trackEvent } from "./analytics";

type Language = "zh" | "en";

interface ErrorState {
  code: string;
  fallback?: string;
}

const LANGUAGE_STORAGE_KEY = "financial-sankey:language:v1";
const acceptedTypes = ".pdf,.html,.htm,.csv,.tsv,.xls,.xlsx,.doc,.docx,.txt,.md";

const translations = {
  zh: {
    htmlLang: "zh-CN",
    brandHome: "SankeyBuddy 首页",
    workspace: "财报分析工作台",
    help: "使用说明",
    mainNav: "主导航",
    uploadTitle: "AI 财报桑基图生成器",
    uploadDescription: "上传利润表或完整财报，AI 将提取数据并校验资金守恒。",
    dropTitle: "拖拽财报到这里",
    dropAction: "或点击选择文件",
    fileSupport: "PDF、HTML/HTM、Excel、CSV、Word · 最大 20 MB",
    replaceFile: "点击更换文件",
    generate: "生成桑基图",
    outputSettings: "图表设置",
    chartLanguage: "图表语言",
    chartUnit: "金额单位",
    autoByMarket: "自动",
    chinese: "中文",
    english: "英文",
    autoHint: "A 股默认中文/亿；港股、美股默认英文/Billion",
    processing: "AI 深度提取中，通常约 30 秒…",
    progress: "生成进度",
    steps: ["读取财报文件", "提取财务数据", "校验资金守恒", "生成桑基图"],
    localBadge: "简化图",
    localBadgeDetail: "AI 提取未成功，本图为基础利润表骨架，缺少分部明细",
    readyTitle: "准备生成桑基图",
    readyDescription: "上传财报后，资金流向图会显示在这里。",
    nodes: "节点",
    flows: "资金流",
    period: "报告期",
    unknownPeriod: "自动识别",
    viewData: "查看数据",
    downloadSvg: "下载 SVG",
    downloadPng: "下载 PNG",
    chartLabel: "桑基图，可左右滚动",
    scrollHint: "左右滑动查看完整图表",
    dataDialog: "提取后的资金流数据",
    helpDialog: "使用说明",
    close: "关闭",
    helpParagraph1:
      "上传 PDF、HTML/HTM、Excel、CSV 或 Word 财报，系统会识别公司、报告期并提取利润表数据。上传文件会限量留存用于失败排查；达到存储上限后自动删除最早文件。",
    helpParagraph2:
      "生成前会校验每个中间节点的流入与流出是否相等。校验通过后可查看原始数据，并下载 SVG 或 PNG。",
    statusReady: "系统就绪",
    statusBusy: "AI 正在处理",
    sourceDemo: "当前显示示例数据",
    sourceGenerated: "当前显示提取结果",
    sourceNone: "尚未生成图表",
    language: "语言",
    switchToChinese: "切换为中文",
    switchToEnglish: "Switch to English",
    examples: "示例",
    examplesTitle: "典型公司示例",
    examplesDescription: "查看不同市场、不同业务结构的利润表桑基图。",
    openExample: "查看大图",
    errors: {
      NO_FILE: "请选择要上传的财报文件。",
      DEMO_LOAD_FAILED: "示例图加载失败，但仍可上传财报生成。",
      RATE_LIMITED: "请求过于频繁，请稍后再试。",
      MISSING_FILE: "请选择财报文件。",
      FILE_TOO_LARGE: "文件不能超过 20 MB。",
      UNSUPPORTED_FILE_TYPE: "不支持该文件格式。",
      INVALID_UPLOAD: "上传请求格式不正确，请重新选择文件后提交。",
      AI_SERVICE_NOT_CONFIGURED: "AI 服务尚未配置，请联系站点管理员。",
      AI_SERVICE_UNAVAILABLE: "AI 服务暂时不可用，请稍后重试。",
      AI_RESPONSE_INVALID: "AI 返回的数据不完整，请重试。",
      INVALID_SANKEY_GRAPH: "数据未通过资金守恒校验，请换一份更完整的财报。",
      INVALID_REPORTING_PERIOD:
        "财报未披露完整的单季度、半年、前九个月或全年利润表，请确认上传文件及报告期。",
      REVENUE_DIMENSION_UNRESOLVED:
        "未能从该财报识别出符合校验的收入科目，请重试。",
      REVENUE_BREAKDOWN_UNRECONCILED:
        "收入构成未能与营业总收入对平，请换一份收入拆分表更完整的财报。",
      EVIDENCE_UNVERIFIABLE:
        "AI 引用的原文无法在财报中逐字校验，通常是表格版式过于复杂，请换一份排版更规整的财报。",
      INCOME_STATEMENT_INCOMPLETE:
        "财报缺少构建资金流所需的完整利润表科目，请上传包含完整利润表的财报。",
      FACT_RECONCILIATION_FAILED:
        "财报中的科目金额无法勾稽（例如毛利不等于收入减成本），请换一份更完整的利润表。",
      GRAPH_QUALITY_REJECTED: "生成的资金流图未通过质量校验，请换一份财报后重试。",
      EXTRACTION_UNBALANCED: "AI 未能从该财报构建出守恒的资金流图，请尝试上传更完整的利润表。",
      GENERATION_FAILED: "AI 提取失败，请稍后重试。",
      UNKNOWN: "生成失败，请稍后重试。",
    },
  },
  en: {
    htmlLang: "en",
    brandHome: "SankeyBuddy home",
    workspace: "Report analysis workspace",
    help: "Help",
    mainNav: "Main navigation",
    uploadTitle: "AI Financial Report Sankey Generator",
    uploadDescription:
      "Upload an income statement or full report. AI extracts the data and validates flow conservation.",
    dropTitle: "Drop a report here",
    dropAction: "or click to choose a file",
    fileSupport: "PDF, HTML/HTM, Excel, CSV, Word · 20 MB max",
    replaceFile: "Click to replace file",
    generate: "Generate Sankey",
    outputSettings: "Chart settings",
    chartLanguage: "Chart language",
    chartUnit: "Amount unit",
    autoByMarket: "Auto",
    chinese: "Chinese",
    english: "English",
    autoHint:
      "A-shares default to Chinese/亿; HK and US default to English/Billion",
    processing: "Deep AI extraction in progress, usually about 30 seconds…",
    progress: "Generation progress",
    steps: ["Read report", "Extract data", "Validate flows", "Generate Sankey"],
    localBadge: "Simplified chart",
    localBadgeDetail:
      "AI extraction was unavailable; this is a basic statement skeleton without segment detail",
    readyTitle: "Ready to generate a Sankey",
    readyDescription: "Your financial flow diagram will appear here after upload.",
    nodes: "Nodes",
    flows: "Flows",
    period: "Period",
    unknownPeriod: "Auto-detected",
    viewData: "View data",
    downloadSvg: "Download SVG",
    downloadPng: "Download PNG",
    chartLabel: "Sankey diagram, horizontally scrollable",
    scrollHint: "Swipe horizontally to view the full chart",
    dataDialog: "Extracted financial flow data",
    helpDialog: "Help",
    close: "Close",
    helpParagraph1:
      "Upload a PDF, HTML/HTM, Excel, CSV, or Word financial report. The system identifies the company and reporting period, then extracts the income statement. Uploads are retained in a size-limited diagnostic archive; the oldest files are removed when the limit is reached.",
    helpParagraph2:
      "Before rendering, every intermediate node is checked for equal inflows and outflows. After validation, you can inspect the data or download SVG and PNG files.",
    statusReady: "System ready",
    statusBusy: "AI is processing",
    sourceDemo: "Showing demo data",
    sourceGenerated: "Showing extracted result",
    sourceNone: "No chart generated",
    language: "Language",
    switchToChinese: "切换为中文",
    switchToEnglish: "Switch to English",
    examples: "Examples",
    examplesTitle: "Company examples",
    examplesDescription: "Explore income-statement Sankey diagrams across markets and business models.",
    openExample: "View full size",
    errors: {
      NO_FILE: "Choose a financial report to upload.",
      DEMO_LOAD_FAILED: "The demo failed to load, but you can still upload a report.",
      RATE_LIMITED: "Too many requests. Please try again later.",
      MISSING_FILE: "Choose a financial report.",
      FILE_TOO_LARGE: "Files must be 20 MB or smaller.",
      UNSUPPORTED_FILE_TYPE: "This file format is not supported.",
      INVALID_UPLOAD: "The upload request was malformed. Choose the file again.",
      AI_SERVICE_NOT_CONFIGURED:
        "AI is not configured. Contact the site administrator.",
      AI_SERVICE_UNAVAILABLE:
        "The AI service is temporarily unavailable. Please try again shortly.",
      AI_RESPONSE_INVALID: "The AI returned incomplete data. Please try again.",
      INVALID_SANKEY_GRAPH:
        "The data failed flow-conservation validation. Try a more complete report.",
      INVALID_REPORTING_PERIOD:
        "This report has no complete quarter, half-year, nine-month, or full-year income statement. Check the file and reporting period.",
      REVENUE_DIMENSION_UNRESOLVED:
        "Could not resolve a revenue line that passes validation. Please try again.",
      REVENUE_BREAKDOWN_UNRECONCILED:
        "The revenue breakdown does not reconcile to total revenue. Try a report with a more complete revenue table.",
      EVIDENCE_UNVERIFIABLE:
        "The AI's quoted excerpts could not be verified word-for-word against the report, usually because the tables are heavily formatted. Try a cleaner report.",
      INCOME_STATEMENT_INCOMPLETE:
        "The report is missing income-statement lines required to build the flow. Upload a report with a complete income statement.",
      FACT_RECONCILIATION_FAILED:
        "The reported amounts do not reconcile (for example gross profit does not equal revenue less cost). Try a more complete income statement.",
      GRAPH_QUALITY_REJECTED:
        "The generated flow graph failed quality validation. Try a different report.",
      EXTRACTION_UNBALANCED:
        "AI could not build a conserved flow graph from this report. Try a more complete income statement.",
      GENERATION_FAILED: "AI extraction failed. Please try again later.",
      UNKNOWN: "Generation failed. Please try again later.",
    },
  },
} as const;

type Translation = (typeof translations)[Language];

function loadLanguage(): Language {
  try {
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (stored === "zh" || stored === "en") return stored;
  } catch {
    // Storage can be unavailable in private browsing.
  }
  return navigator.language.toLowerCase().startsWith("zh") ? "zh" : "en";
}

function saveLanguage(language: Language): void {
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
  } catch {
    // Language switching still works for the current session.
  }
}

function errorMessage(error: ErrorState | null, language: Language): string {
  if (!error) return "";
  const messages = translations[language].errors;
  return messages[error.code as keyof typeof messages] ?? error.fallback ?? messages.UNKNOWN;
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

function slug(title: string): string {
  return (
    title
      .toLowerCase()
      .replace(/[^a-z0-9\u4e00-\u9fff]+/g, "-")
      .replace(/^-|-$/g, "") || "sankeybuddy"
  );
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

interface FeaturedReport {
  slug: string;
  company: string;
  period: string;
  insight: string;
  href: string;
}

// The server injects the current content directory as a JSON island, so adding
// a report page never requires editing this file.
function readFeaturedReports(): FeaturedReport[] {
  const island = document.getElementById("report-index");
  if (!island?.textContent) return [];
  try {
    const parsed: unknown = JSON.parse(island.textContent);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (entry): entry is FeaturedReport =>
        typeof entry === "object" &&
        entry !== null &&
        typeof (entry as FeaturedReport).slug === "string" &&
        typeof (entry as FeaturedReport).href === "string",
    );
  } catch {
    return [];
  }
}

const featuredReports = readFeaturedReports();

function SeoContent({ language }: { language: Language }) {
  const chinese = language === "zh";
  const steps = chinese
    ? [
        ["01", "读取财报", "支持 PDF、HTML、Excel、CSV 和 Word"],
        ["02", "提取事实", "识别公司、报告期、币种与利润表科目"],
        ["03", "数据校验", "核对会计恒等式、来源明细与流量守恒"],
        ["04", "生成图表", "查看结构化数据并下载 SVG 或 PNG"],
      ]
    : [
        ["01", "Read report", "PDF, HTML, Excel, CSV, and Word"],
        ["02", "Extract facts", "Company, period, currency, and statement items"],
        ["03", "Validate data", "Accounting identities and flow conservation"],
        ["04", "Render chart", "Inspect data and download SVG or PNG"],
      ];
  return (
    <main className="seo-content" id="about">
      <section className="seo-introduction">
        <p className="seo-eyebrow">SankeyBuddy · Financial report visualization</p>
        <h2>
          {chinese
            ? "把复杂利润表变成可复核的财务桑基图"
            : "Turn complex income statements into verifiable Sankey diagrams"}
        </h2>
        <p>
          {chinese
            ? "上传 A 股、港股或美股财报，系统从原始报告中提取营收、成本、费用与利润，先校验会计关系和资金守恒，再生成可下载的 SVG 或 PNG 桑基图。"
            : "Upload an A-share, Hong Kong, or US financial report. SankeyBuddy extracts revenue, costs, expenses, and profit, validates the accounting relationships and flow conservation, then produces downloadable SVG and PNG diagrams."}
        </p>
        <div className="seo-steps" aria-label={chinese ? "生成流程" : "Generation workflow"}>
          {steps.map(([number, title, description]) => (
            <article key={number}>
              <span>{number}</span>
              <h3>{title}</h3>
              <p>{description}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="seo-reports" id="reports">
        <div>
          <p className="seo-eyebrow">{chinese ? "财报案例" : "Report examples"}</p>
          <h2>{chinese ? "近期财报桑基图与利润分析" : "Recent report Sankey diagrams"}</h2>
        </div>
        <div className="seo-report-grid">
          {featuredReports.map((report) => (
            <a href={report.href} key={report.href}>
              <span>{report.period}</span>
              <h3>{report.company}</h3>
              <p>{report.insight}</p>
              <strong>{chinese ? "查看财报拆解 →" : "Read analysis →"}</strong>
            </a>
          ))}
        </div>
        <p className="seo-reports-more">
          <a href="reports/">
            {chinese ? "查看全部财报桑基图 →" : "Browse every report Sankey diagram →"}
          </a>
        </p>
      </section>

      <section className="seo-faq" id="faq">
        <p className="seo-eyebrow">{chinese ? "常见问题" : "Frequently asked questions"}</p>
        <h2>{chinese ? "关于财报提取与桑基图生成" : "Financial extraction and Sankey generation"}</h2>
        <details>
          <summary>{chinese ? "支持哪些财报格式？" : "Which report formats are supported?"}</summary>
          <p>
            {chinese
              ? "支持 PDF、HTML/HTM、Excel、CSV、Word、Markdown 和纯文本，单个文件最大 20 MB。"
              : "PDF, HTML/HTM, Excel, CSV, Word, Markdown, and plain text are supported, up to 20 MB per file."}
          </p>
        </details>
        <details>
          <summary>{chinese ? "生成前会校验数据吗？" : "Is the data validated before rendering?"}</summary>
          <p>
            {chinese
              ? "会。系统校验报告期、单位、利润阶段、收入来源对账、会计恒等式以及每个内部节点的流入与流出。只有通过验证的数据才用于绘图。"
              : "Yes. SankeyBuddy checks the reporting period, units, profit stages, revenue reconciliation, accounting identities, and every internal node's inflows and outflows before rendering."}
          </p>
        </details>
        <details>
          <summary>{chinese ? "上传的财报会自动公开吗？" : "Are uploaded reports published automatically?"}</summary>
          <p>
            {chinese
              ? "不会。普通用户上传文件仅用于生成和限量诊断留存；网站公开案例只来自主动筛选的公开财报。"
              : "No. User uploads are used only for generation and size-limited diagnostics. Public examples are curated from publicly disclosed reports."}
          </p>
        </details>
      </section>
    </main>
  );
}

function Dialog({
  open,
  title,
  closeLabel,
  className,
  onClose,
  children,
}: {
  open: boolean;
  title: string;
  closeLabel: string;
  className?: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);
  return (
    <dialog
      ref={dialogRef}
      className={className ? `dialog ${className}` : "dialog"}
      onClose={onClose}
    >
      <div className="dialog-heading">
        <h2>{title}</h2>
        <button className="icon-button" type="button" onClick={onClose} aria-label={closeLabel}>
          <CloseIcon />
        </button>
      </div>
      {children}
    </dialog>
  );
}

const Progress = memo(function Progress({
  activeStep,
  busy,
  labels,
  ariaLabel,
}: {
  activeStep: number;
  busy: boolean;
  labels: readonly string[];
  ariaLabel: string;
}) {
  return (
    <ol className="progress" aria-label={ariaLabel} aria-live="polite">
      {labels.map((label, index) => {
        const complete = index < activeStep || (!busy && activeStep === 3);
        const active = index === activeStep && !complete;
        return (
          <li
            className={complete ? "is-complete" : active ? "is-active" : ""}
            key={label}
          >
            <span className="progress-dot">
              {complete ? <CheckIcon /> : index + 1}
            </span>
            <span className="progress-label">{label}</span>
            <span className="progress-state" aria-hidden="true">
              {complete ? <CheckIcon /> : null}
            </span>
          </li>
        );
      })}
    </ol>
  );
});

function FileDrop({
  file,
  text,
  onSelect,
}: {
  file: File | null;
  text: Translation;
  onSelect: (file: File) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragging(false);
    const dropped = event.dataTransfer.files[0];
    if (dropped) onSelect(dropped);
  };
  return (
    <div
      className={`file-drop ${dragging ? "is-dragging" : ""}`}
      onDragEnter={(event) => {
        event.preventDefault();
        setDragging(true);
      }}
      onDragOver={(event) => event.preventDefault()}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
    >
      <button
        className="file-drop-trigger"
        type="button"
        onClick={() => inputRef.current?.click()}
      >
        <UploadIcon />
        <span>{text.dropTitle}</span>
        <small>{text.dropAction}</small>
        <em>{text.fileSupport}</em>
      </button>
      <input
        ref={inputRef}
        type="file"
        accept={acceptedTypes}
        onChange={(event) => {
          const selected = event.target.files?.[0];
          if (selected) onSelect(selected);
        }}
        hidden
      />
      {file ? (
        <button
          type="button"
          className="selected-file"
          onClick={() => inputRef.current?.click()}
          title={text.replaceFile}
        >
          <span className="file-check"><CheckIcon /></span>
          <span className="file-copy">
            <strong>{file.name}</strong>
            <small>{formatFileSize(file.size)}</small>
          </span>
          <span className="replace-label">{text.replaceFile}</span>
        </button>
      ) : null}
    </div>
  );
}

function InputPanel({
  busy,
  activeStep,
  text,
  onGenerate,
}: {
  busy: boolean;
  activeStep: number;
  text: Translation;
  onGenerate: (
    file: File | null,
    preferences: GenerationPreferences,
  ) => Promise<void>;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [outputLanguage, setOutputLanguage] =
    useState<OutputLanguage>("auto");
  const [outputUnit, setOutputUnit] = useState<OutputUnit>("auto");
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    await onGenerate(file, {
      language: outputLanguage,
      unit: outputUnit,
    });
  };
  return (
    <aside className="input-panel" id="upload">
      <div className="intro">
        <h1>{text.uploadTitle}</h1>
        <p>{text.uploadDescription}</p>
      </div>
      <form onSubmit={submit}>
        <FileDrop file={file} text={text} onSelect={setFile} />
        <fieldset className="output-settings">
          <legend>{text.outputSettings}</legend>
          <div className="setting-row">
            <span>{text.chartLanguage}</span>
            <div className="segmented-control">
              {(["auto", "zh", "en"] as const).map((value) => (
                <button
                  type="button"
                  className={outputLanguage === value ? "is-selected" : ""}
                  aria-pressed={outputLanguage === value}
                  onClick={() => setOutputLanguage(value)}
                  key={value}
                >
                  {value === "auto"
                    ? text.autoByMarket
                    : value === "zh"
                      ? text.chinese
                      : text.english}
                </button>
              ))}
            </div>
          </div>
          <div className="setting-row">
            <span>{text.chartUnit}</span>
            <div className="segmented-control">
              {(["auto", "B", "亿"] as const).map((value) => (
                <button
                  type="button"
                  className={outputUnit === value ? "is-selected" : ""}
                  aria-pressed={outputUnit === value}
                  onClick={() => setOutputUnit(value)}
                  key={value}
                >
                  {value === "auto"
                    ? text.autoByMarket
                    : value === "B"
                      ? "Billion"
                      : value}
                </button>
              ))}
            </div>
          </div>
          <small>{text.autoHint}</small>
        </fieldset>
        <button className="primary-button" type="submit" disabled={busy}>
          {busy ? text.processing : text.generate}
          <span aria-hidden="true">→</span>
        </button>
      </form>
      <Progress
        activeStep={activeStep}
        busy={busy}
        labels={text.steps}
        ariaLabel={text.progress}
      />
    </aside>
  );
}

const ChartCanvas = memo(function ChartCanvas({
  svg,
  ariaLabel,
  scrollHint,
}: {
  svg: string;
  ariaLabel: string;
  scrollHint: string;
}) {
  return (
    <>
      <div className="chart-viewport" tabIndex={0} aria-label={ariaLabel}>
        <div
          className="chart-inner"
          // The SVG is generated by our renderer, which XML-escapes all model text.
          dangerouslySetInnerHTML={{ __html: svg }}
        />
      </div>
      <p className="scroll-hint">{scrollHint}</p>
    </>
  );
});

function ResultPanel({
  result,
  busy,
  error,
  text,
  onShowData,
}: {
  result: GenerationResult | null;
  busy: boolean;
  error: string;
  text: Translation;
  onShowData: () => void;
}) {
  const downloadSvg = useCallback(() => {
    if (!result) return;
    downloadBlob(
      new Blob([result.svg], { type: "image/svg+xml;charset=utf-8" }),
      `${slug(result.document.title)}.svg`,
    );
  }, [result]);

  const downloadPng = useCallback(async () => {
    if (!result) return;
    const blob = await renderPng(result.document);
    downloadBlob(blob, `${slug(result.document.title)}.png`);
  }, [result]);

  return (
    <main className="result-panel">
      {error ? <div className="error-banner" role="alert">{error}</div> : null}
      <section className={`result-content ${busy ? "is-busy" : ""}`}>
        <header className="result-heading">
          <div className="report-identity">
            <h2>
              {result?.document.title ?? text.readyTitle}
              {result?.document.ai_provider === "local" ? (
                <span
                  className="local-badge"
                  title={text.localBadgeDetail}
                >
                  {text.localBadge}
                </span>
              ) : null}
            </h2>
            <p>
              {result?.document.company ?? "SankeyBuddy"}
              <span aria-hidden="true">·</span>
              {result?.document.period_label ?? text.unknownPeriod}
            </p>
          </div>
          <dl className="report-metrics">
            <div>
              <dt>{text.nodes}</dt>
              <dd>{result?.stats.nodes ?? "—"}</dd>
            </div>
            <div>
              <dt>{text.flows}</dt>
              <dd>{result?.stats.flows ?? "—"}</dd>
            </div>
            <div>
              <dt>{text.period}</dt>
              <dd>{result?.document.period_label ?? "—"}</dd>
            </div>
          </dl>
        </header>
        <div className="canvas-toolbar">
          <div className="result-actions">
            <button
              type="button"
              aria-label={text.viewData}
              onClick={onShowData}
              disabled={!result}
            >
              <DataIcon /><span>{text.viewData}</span>
            </button>
            <button
              type="button"
              aria-label={text.downloadSvg}
              onClick={downloadSvg}
              disabled={!result}
            >
              <DownloadIcon /><span>SVG</span>
            </button>
            <button
              type="button"
              aria-label={text.downloadPng}
              onClick={downloadPng}
              disabled={!result}
            >
              <DownloadIcon /><span>PNG</span>
            </button>
          </div>
        </div>
        {result ? (
          <ChartCanvas
            svg={result.svg}
            ariaLabel={text.chartLabel}
            scrollHint={text.scrollHint}
          />
        ) : (
          <div className="empty-state">
            <span className="empty-mark"><BrandLogo /></span>
            <h3>{text.readyTitle}</h3>
            <p>{text.readyDescription}</p>
          </div>
        )}
      </section>
    </main>
  );
}

export function App() {
  const [language, setLanguage] = useState<Language>(() => loadLanguage());
  const [result, setResult] = useState<GenerationResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [error, setError] = useState<ErrorState | null>(null);
  const [showData, setShowData] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [showExamples, setShowExamples] = useState(false);
  const [generated, setGenerated] = useState(false);
  const text = translations[language];

  useEffect(() => {
    document.documentElement.lang = text.htmlLang;
  }, [text.htmlLang]);

  useEffect(() => {
    trackEvent("page_view");
  }, []);

  useEffect(() => {
    let active = true;
    loadDemo()
      .then((demo) => {
        if (!active) return;
        setResult(demo);
        setActiveStep(3);
      })
      .catch(() => {
        if (active) setError({ code: "DEMO_LOAD_FAILED" });
      });
    return () => {
      active = false;
    };
  }, []);

  const changeLanguage = (nextLanguage: Language) => {
    setLanguage(nextLanguage);
    saveLanguage(nextLanguage);
  };

  const generate = useCallback(
    async (
      file: File | null,
      preferences: GenerationPreferences,
    ) => {
      if (busy) return;
      if (!file) {
        setError({ code: "NO_FILE" });
        return;
      }
      // The reverse proxy rejects an oversized body before the upload reaches
      // the app, so catch it here — otherwise the visitor waits out a full
      // upload only to be told to try again, and tries the same file again.
      if (file.size > MAX_UPLOAD_BYTES) {
        setError({ code: "FILE_TOO_LARGE" });
        trackEvent("generation_started", { detail: file.type || file.name.split(".").pop() });
        trackEvent("generation_failed", { duration_ms: 0, error_code: "FILE_TOO_LARGE" });
        return;
      }
      setBusy(true);
      setResult(null);
      setGenerated(false);
      setShowData(false);
      const startedAt = performance.now();
      trackEvent("generation_started", { detail: file.type || file.name.split(".").pop() });
      setError(null);
      setActiveStep(0);
      // Reading is quick; deep extraction dominates (60-150s), validation runs
      // near the end server-side.
      const timers = [
        window.setTimeout(() => setActiveStep(1), 2_000),
        window.setTimeout(() => setActiveStep(2), 100_000),
      ];
      try {
        const next = await generateFromUpload(file, preferences);
        setActiveStep(3);
        setResult(next);
        setGenerated(true);
        trackEvent("generation_succeeded", { duration_ms: performance.now() - startedAt });
      } catch (generationError) {
        setError(
          generationError instanceof ApiRequestError
            ? {
                code: generationError.code || "UNKNOWN",
                fallback: generationError.message,
              }
            : { code: "UNKNOWN" },
        );
        setActiveStep(0);
        trackEvent("generation_failed", {
          duration_ms: performance.now() - startedAt,
          error_code: generationError instanceof ApiRequestError ? generationError.code : "UNKNOWN",
        });
      } finally {
        for (const timer of timers) window.clearTimeout(timer);
        setBusy(false);
      }
    },
    [busy],
  );

  return (
    <div className="app-shell">
      <header className="site-header">
        <div className="header-left">
          <a className="brand" href="./" aria-label={text.brandHome}>
            <span className="brand-mark"><BrandLogo /></span>
            <span>SankeyBuddy</span>
          </a>
          <span className="header-divider" aria-hidden="true" />
          <span className="workspace-name">{text.workspace}</span>
          {result ? (
            <span className="document-chip" title={result.document.title}>
              {result.document.title}
              <span className="live-dot" aria-hidden="true" />
            </span>
          ) : null}
        </div>
        <nav aria-label={text.mainNav}>
          <div className="language-switch" aria-label={text.language}>
            <button
              type="button"
              className={language === "zh" ? "is-selected" : ""}
              aria-pressed={language === "zh"}
              aria-label={text.switchToChinese}
              onClick={() => changeLanguage("zh")}
            >
              中文
            </button>
            <button
              type="button"
              className={language === "en" ? "is-selected" : ""}
              aria-pressed={language === "en"}
              aria-label={text.switchToEnglish}
              onClick={() => changeLanguage("en")}
            >
              EN
            </button>
          </div>
          <button type="button" className="help-button" onClick={() => setShowExamples(true)}>
            {text.examples}
          </button>
          <a className="help-button" href="#reports">
            {language === "zh" ? "财报文章" : "Reports"}
          </a>
          <button type="button" className="help-button" onClick={() => setShowHelp(true)}>
            <HelpIcon />{text.help}
          </button>
        </nav>
      </header>
      <div className="workspace" id="generator">
        <InputPanel
          busy={busy}
          activeStep={activeStep}
          text={text}
          onGenerate={generate}
        />
        <ResultPanel
          result={result}
          busy={busy}
          error={errorMessage(error, language)}
          text={text}
          onShowData={() => setShowData(true)}
        />
        <footer className="status-bar">
          <span className={busy ? "status-indicator is-busy" : "status-indicator"} />
          <span>{busy ? text.statusBusy : text.statusReady}</span>
          <span className="status-spacer" />
          <span>
            {result
              ? generated
                ? text.sourceGenerated
                : text.sourceDemo
              : text.sourceNone}
          </span>
          {result ? (
            <>
              <span>{text.nodes}: {result.stats.nodes}</span>
              <span>{text.flows}: {result.stats.flows}</span>
            </>
          ) : null}
        </footer>
      </div>
      <SeoContent language={language} />
      <Dialog
        open={showData}
        title={text.dataDialog}
        closeLabel={text.close}
        onClose={() => setShowData(false)}
      >
        <pre>{result ? JSON.stringify(result.document, null, 2) : ""}</pre>
      </Dialog>
      <Dialog
        open={showExamples}
        title={text.examplesTitle}
        closeLabel={text.close}
        className="dialog--examples"
        onClose={() => setShowExamples(false)}
      >
        <div className="examples-content">
          <p className="examples-description">{text.examplesDescription}</p>
          <div className="example-grid">
            {[
              ["Apple", "Q2 2026", "examples/apple-q2-2026.png"],
              ["Tencent", "Q1 2026", "examples/tencent-q1-2026.png"],
              ["贵州茅台", "2026 年第一季度", "examples/maotai-2026-q1.png"],
            ].map(([company, period, image]) => (
              <a className="example-card" href={image} target="_blank" rel="noreferrer" key={company}>
                <img src={image} alt={`${company} ${period}`} loading="lazy" />
                <span><strong>{company}</strong><small>{period}</small></span>
                <em>{text.openExample} ↗</em>
              </a>
            ))}
          </div>
        </div>
      </Dialog>
      <Dialog
        open={showHelp}
        title={text.helpDialog}
        closeLabel={text.close}
        onClose={() => setShowHelp(false)}
      >
        <div className="help-copy">
          <p>{text.helpParagraph1}</p>
          <p>{text.helpParagraph2}</p>
        </div>
      </Dialog>
    </div>
  );
}
